import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { IMAGES, GALLERY_TAGS, getPhotoTag } from "../lib/api";
import { useLanguage } from "../lib/i18n";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import LazyImage from "../components/LazyImage";

const Gallery = () => {
    const { t } = useLanguage();
    const [activeIndex, setActiveIndex] = useState(null);
    const [filter, setFilter] = useState("all");
    const touchStart = useRef(null);

    const photos = useMemo(() => {
        if (filter === "all") return IMAGES.gallery;
        return IMAGES.gallery.filter((url) => getPhotoTag(url) === filter);
    }, [filter]);

    // Reset active index if it goes out of bounds when filter changes
    useEffect(() => {
        if (activeIndex !== null && activeIndex >= photos.length) {
            setActiveIndex(null);
        }
    }, [activeIndex, photos.length]);

    const close = useCallback(() => setActiveIndex(null), []);
    const next = useCallback(
        () => setActiveIndex((i) => (i === null ? null : (i + 1) % photos.length)),
        [photos.length]
    );
    const prev = useCallback(
        () =>
            setActiveIndex((i) =>
                i === null ? null : (i - 1 + photos.length) % photos.length
            ),
        [photos.length]
    );

    useEffect(() => {
        if (activeIndex === null) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [activeIndex, close, next, prev]);

    const onTouchStart = (e) => (touchStart.current = e.touches[0].clientX);
    const onTouchEnd = (e) => {
        if (touchStart.current === null) return;
        const dx = e.changedTouches[0].clientX - touchStart.current;
        if (Math.abs(dx) > 50) (dx < 0 ? next : prev)();
        touchStart.current = null;
    };

    return (
        <div data-testid="gallery-page" className="pt-32 pb-24 bg-background">
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="overline text-secondary mb-4">{t("page.gallery.overline")}</div>
                <h1 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-primary leading-[0.95] tracking-tight max-w-3xl">
                    {t("page.gallery.title")}
                </h1>
                <p className="mt-6 max-w-xl text-muted-foreground font-light leading-relaxed">
                    {t("page.gallery.intro")}
                </p>

                {/* Filter chips */}
                <div className="mt-10 flex flex-wrap gap-2">
                    {GALLERY_TAGS.map((tag) => {
                        const count =
                            tag === "all"
                                ? IMAGES.gallery.length
                                : IMAGES.gallery.filter(
                                      (u) => getPhotoTag(u) === tag
                                  ).length;
                        if (count === 0 && tag !== "all") return null;
                        const isActive = filter === tag;
                        return (
                            <button
                                key={tag}
                                onClick={() => setFilter(tag)}
                                data-testid={`gallery-filter-${tag}`}
                                className={`px-5 py-2.5 text-xs tracking-[0.2em] uppercase border transition-all ${
                                    isActive
                                        ? "bg-primary text-white border-primary"
                                        : "bg-transparent text-foreground border-border hover:border-primary"
                                }`}
                            >
                                {t(`filter.${tag}`)}
                                <span className="ml-2 opacity-60">{count}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mt-12 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                {photos.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground font-light">
                        No photos in this category yet.
                    </div>
                ) : (
                    <div className="columns-1 sm:columns-2 lg:columns-3 gap-2 [&>*]:mb-2">
                        {photos.map((url, i) => (
                            <button
                                key={`${filter}-${url}`}
                                data-testid={`gallery-item-${i}`}
                                onClick={() => setActiveIndex(i)}
                                className="block w-full overflow-hidden group cursor-zoom-in"
                            >
                                <LazyImage
                                    src={url}
                                    alt={`Villa Alam ${i + 1}`}
                                    className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-105"
                                />
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {activeIndex !== null && photos[activeIndex] && (
                <div
                    data-testid="gallery-lightbox"
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                    className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
                >
                    <button
                        data-testid="lightbox-close"
                        className="absolute top-6 right-6 text-white/80 hover:text-white p-2 z-10"
                        onClick={close}
                        aria-label="Close"
                    >
                        <X size={28} />
                    </button>

                    <button
                        data-testid="lightbox-prev"
                        onClick={prev}
                        aria-label="Previous photo"
                        className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
                    >
                        <ChevronLeft size={36} strokeWidth={1.5} />
                    </button>
                    <button
                        data-testid="lightbox-next"
                        onClick={next}
                        aria-label="Next photo"
                        className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-3 hover:bg-white/10 rounded-full transition-colors z-10"
                    >
                        <ChevronRight size={36} strokeWidth={1.5} />
                    </button>

                    <div
                        className="w-full h-full flex items-center justify-center cursor-zoom-out"
                        onClick={close}
                    >
                        <img
                            key={activeIndex}
                            src={photos[activeIndex]}
                            alt={`Villa Alam ${activeIndex + 1}`}
                            onClick={(e) => e.stopPropagation()}
                            className="max-h-[88vh] max-w-[92vw] object-contain animate-in fade-in duration-300"
                        />
                    </div>

                    <div className="absolute bottom-6 inset-x-0 text-center text-white/60 text-xs tracking-[0.2em] uppercase pointer-events-none">
                        {activeIndex + 1} / {photos.length}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Gallery;
